import React, { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { RoundedButton } from '../RoundedButton/RoundedButton';
import { SearchIcon } from '../Icons/SearchIcon';
import { CircularProgress, useMediaQuery } from '@material-ui/core';

const SELECTED_COLOR = '#92b2ef';

const SearchBarStyled = styled.div<{ focused: boolean | undefined }>`
    position: relative;
    box-sizing: border-box;
    padding: 8px;
    background: #fff;
    border: 1px solid #e4e6e8;
    border-radius: 62px;
    box-shadow: 2px 2px 18px rgba(0, 0, 0, 0.1);
    transition: all 0.2s linear;

    ${props =>
        props.focused &&
        css`
            border-color: #92b2ef;
            box-shadow: 2px 2px 18px rgba(29, 87, 199, 0.25);
        `};

    > svg {
        position: absolute;
        top: 50%;
        left: 24px;
        transform: translateY(-50%);
    }

    form {
        display: flex;
        align-items: center;
        justify-content: flex-start;

        input,
        button {
            margin: 0 8px;
        }

        input {
            flex: 1 0 0;
            height: 50px;
            margin-left: 56px;
            font-size: 16px;
            border: none;
            outline: none;
        }

        button {
            flex: 0 1 0;
        }
    }
`;

interface SearchBarProps {
    placeholder: string;
    value: string;
    loading?: boolean;
    onChange?: (value: string) => void;
    onSearch?: (value: string) => void;
}

export const SearchBar = ({
    value,
    placeholder,
    onChange,
    onSearch,
    loading,
}: SearchBarProps): JSX.Element => {
    const [focused, setFocused] = useState(false);
    const [query, setQuery] = useState(value);
    const mediaQuery = useMediaQuery('(min-width: 768px)');

    useEffect(() => {
        if (onChange) {
            onChange(query);
        }
    }, [onChange, query]);

    return (
        <SearchBarStyled focused={focused}>
            <SearchIcon fill={focused ? SELECTED_COLOR : undefined} />
            <form
                onSubmit={e => {
                    e.preventDefault();
                    onSearch(query);
                }}
            >
                <input
                    type={'text'}
                    value={query}
                    placeholder={placeholder}
                    onChange={e => setQuery(e.target.value)}
                    onFocus={() => setFocused(true)}
                    onBlur={() => setFocused(false)}
                />
                {mediaQuery && (
                    <RoundedButton
                        variant="contained"
                        color="primary"
                        onClick={() => onSearch(query)}
                        size="large"
                        disableElevation
                    >
                        {loading ? <CircularProgress color={'inherit'} size={16} /> : 'Search'}
                    </RoundedButton>
                )}
            </form>
        </SearchBarStyled>
    );
};
